import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowPath,
  heroBolt,
  heroClock,
  heroCog6Tooth,
  heroCpuChip,
  heroDocumentText,
} from '@ng-icons/heroicons/outline';
import { forkJoin } from 'rxjs';
import type { Agent } from '../../../core/api/generated/models/Agent';
import type { AgentArchetype } from '../../../core/api/generated/models/AgentArchetype';
import type { AgentStatus } from '../../../core/api/generated/models/AgentStatus';
import type { AgentVersion } from '../../../core/api/generated/models/AgentVersion';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiDetailHeaderSkeleton,
  UiEmptyState,
  UiPageHeader,
  UiRouteTabLink,
  UiRouteTabPanel,
  UiRouteTabs,
} from '../../../shared/ui';
import { AgentsApiService } from '../agents-api.service';
import { AGENT_DETAIL_CONTEXT } from './agent-detail.context';

const AGENT_STATUS_LABELS: Record<AgentStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  disabled: 'Desativado',
  archived: 'Arquivado',
};

type TabLink = {
  readonly path: string | null;
  readonly label: string;
  readonly iconRef: string;
  readonly exact: boolean;
};

@Component({
  selector: 'app-agent-detail-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgIcon,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    UiBadge,
    UiButton,
    UiDetailHeaderSkeleton,
    UiEmptyState,
    UiPageHeader,
    UiRouteTabLink,
    UiRouteTabPanel,
    UiRouteTabs,
  ],
  providers: [
    provideIcons({
      heroArrowPath,
      heroBolt,
      heroClock,
      heroCog6Tooth,
      heroCpuChip,
      heroDocumentText,
    }),
    {
      provide: AGENT_DETAIL_CONTEXT,
      useFactory: () => inject(AgentDetailPage).context(),
    },
  ],
  templateUrl: './agent-detail.page.html',
  styleUrl: './agent-detail.page.css',
})
export class AgentDetailPage {
  private readonly api = inject(AgentsApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastrService);

  readonly loading = signal(true);
  readonly agent = signal<Agent | null>(null);
  readonly versions = signal<AgentVersion[]>([]);
  readonly archetypes = signal<AgentArchetype[]>([]);

  readonly archetype = computed(() => {
    const agent = this.agent();
    if (!agent?.archetypeId) return null;
    return this.archetypes().find((item) => item.id === agent.archetypeId) ?? null;
  });

  readonly currentVersion = computed(() => {
    const agent = this.agent();
    if (!agent?.currentVersionId) return null;
    return this.versions().find((version) => version.id === agent.currentVersionId) ?? null;
  });

  readonly tabs = computed<readonly TabLink[]>(() => {
    const agentId = this.agent()?.id ?? this.route.snapshot.paramMap.get('agentId');
    const prefix = agentId ? `/agentes/${agentId}` : null;
    return [
      { path: prefix, label: 'Visão geral', iconRef: 'heroCpuChip', exact: true },
      { path: prefix ? `${prefix}/configuracao` : null, label: 'Configuração', iconRef: 'heroCog6Tooth', exact: false },
      { path: prefix ? `${prefix}/missao` : null, label: 'Missão', iconRef: 'heroDocumentText', exact: false },
      { path: prefix ? `${prefix}/versoes` : null, label: 'Versões', iconRef: 'heroClock', exact: false },
      { path: prefix ? `${prefix}/execucoes` : null, label: 'Execuções', iconRef: 'heroBolt', exact: false },
    ];
  });

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const agentId = params.get('agentId');
      if (agentId) this.load(agentId);
    });
  }

  reload(): void {
    const agentId = this.agent()?.id ?? this.route.snapshot.paramMap.get('agentId');
    if (agentId) this.load(agentId);
  }

  statusLabel(status: AgentStatus): string {
    return AGENT_STATUS_LABELS[status] ?? status;
  }

  context() {
    return {
      agent: this.agent,
      versions: this.versions,
      archetypes: this.archetypes,
      archetype: this.archetype,
      currentVersion: this.currentVersion,
      reload: () => this.reload(),
    };
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return;
    }
    if (event.key === 'r' || event.key === 'R') {
      event.preventDefault();
      this.reload();
    } else if (event.key === 'Escape') {
      void this.router.navigate(['/agentes']);
    }
  }

  private load(agentId: string): void {
    this.loading.set(true);
    forkJoin({
      agent: this.api.getAgent(agentId),
      versions: this.api.listVersions(agentId),
      archetypes: this.api.listArchetypes(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ agent, versions, archetypes }) => {
          this.agent.set(agent);
          this.versions.set(versions.data);
          this.archetypes.set(archetypes.data);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toast.error('Não foi possível carregar o agente.');
          void this.router.navigate(['/agentes']);
        },
      });
  }
}
